import React from "react";
import { Icon, notify, confirmDialog } from "./registry";
import { roleLabel, roleOf } from "./lib/roles";
import {
  readSupport, enablePush, disablePush, syncPushDevice, pushOnHere, deviceStatus, listenForPushClicks, thisDeviceLabel,
  showPromptCard, PROMPT_KEY, type Support, type EnableResult,
} from "./lib/push";

/* Phone + desktop notifications (server/src/push.js, lib/push.ts).

   NotificationsPanel  Account → Notifications: turn this device on or off,
                       what buzzes (per person), the devices that are on,
                       "Send a test". On an iPhone outside the Home Screen
                       app it says plainly that Apple only allows it there,
                       with the steps.
   PushPromptCard      the dashboard's soft ask — only after the first lead
                       or signed quote, never before; "Not now" is quiet for
                       30 days. The browser's own permission prompt only ever
                       follows a tap on one of these buttons. */

// a tapped notification with the app already open routes this tab (sw.js)
listenForPushClicks();

const pushApi = () => { const DB: any = (window as any).VFP_DB; return DB && DB.push && DB.push.enabled && DB.push.enabled() ? DB.push : null; };
const inSandbox = () => !!((window as any).VFP_DEMO && (window as any).VFP_DEMO.active);

/* one /push/config per page load is plenty for the dashboard card */
let configOnce: Promise<any> | null = null;
function loadConfig(api: any, fresh = false): Promise<any> {
  if (!configOnce || fresh) configOnce = api.config().catch((e: any) => { configOnce = null; throw e; });
  return configOnce as Promise<any>;
}

/* what to say when turning on didn't work */
function enableProblem(r: EnableResult): string {
  switch (r.reason) {
    case "denied": return "Notifications are blocked for Fence Works in this browser. Allow them in the site settings (the icon left of the address bar), then turn them on here.";
    case "dismissed": return "The browser didn't turn them on. Tap Turn on again and choose Allow.";
    case "ios-install": return "On iPhone, add Fence Works to your Home Screen first — the steps are below.";
    case "ios-old": return "iPhone notifications need iOS 16.4 or later.";
    case "unsupported": return "This browser can't show notifications.";
    default: return r.message || "Couldn't turn notifications on. Try again in a minute.";
  }
}

/* iOS's Share glyph, so the step points at the button they'll actually see */
const ShareGlyph = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-label="Share" role="img" style={{ verticalAlign: "-2px" }}>
    <path d="M12 3v12M8 7l4-4 4 4" /><path d="M6 11H5a1 1 0 00-1 1v8a1 1 0 001 1h14a1 1 0 001-1v-8a1 1 0 00-1-1h-1" />
  </svg>
);

export function IosHomeScreenNote({ compact = false }: any) {
  return (
    <div className="push-ios" role="note">
      <div className="row gap-8" style={{ alignItems: "flex-start" }}>
        <Icon name="phone" size={16} />
        <div style={{ minWidth: 0 }}>
          <b>On iPhone, notifications need the app on your Home Screen</b>
          <p className="muted">Apple only lets a website send notifications once it's added to the Home Screen (iPhone with iOS 16.4 or later). It takes a few seconds:</p>
          <ol>
            <li>In Safari, tap <b>Share</b> <ShareGlyph /> at the bottom of the screen.</li>
            <li>Scroll down, tap <b>Add to Home Screen</b>, then <b>Add</b>.</li>
            <li>Open <b>Fence Works</b> from your Home Screen and sign in. Tap the bell at the top, then <b>Phone notifications</b>, and turn them on.</li>
          </ol>
          {!compact && <p className="faint" style={{ margin: 0 }}>Already added it? Open it from the Home Screen icon — notifications can't be turned on from a Safari tab.</p>}
        </div>
      </div>
    </div>
  );
}

function SupportNote({ support }: { support: Support }) {
  if (support.state === "ios-install") return <IosHomeScreenNote />;
  const text: Record<string, string> = {
    "ios-old": "iPhone notifications need iOS 16.4 or later. Update in Settings → General → Software Update, then add Fence Works to your Home Screen.",
    unsupported: "This browser can't show notifications. Chrome, Edge, Firefox and Safari can — or add Fence Works to your phone's Home Screen and turn them on there.",
    insecure: "Notifications only work on the secure app address (https://app.fenceworks.ai).",
    denied: "Notifications are blocked for Fence Works in this browser. Allow them in the site settings (the icon left of the address bar), then reload this page and turn them on.",
  };
  if (!text[support.state]) return null;
  return <p className="push-note" role="note"><Icon name="bell" size={14} />{text[support.state]}</p>;
}

export function NotificationsPanel({ store }: any) {
  const api = pushApi();
  const [support, setSupport] = React.useState<Support>(() => readSupport());
  const [cfg, setCfg] = React.useState<any>(null);
  const [prefs, setPrefs] = React.useState<any>(null);
  const [devices, setDevices] = React.useState<any>(null);
  const [here, setHere] = React.useState<any>({ subscribed: pushOnHere(), key: "" });
  const [busy, setBusy] = React.useState<string>("");
  const [err, setErr] = React.useState<string>("");
  const [loadErr, setLoadErr] = React.useState<string>("");

  async function load() {
    if (!api) return;
    setLoadErr("");
    try {
      const c = await loadConfig(api, true);
      setCfg(c);
      if (c.setup === false) return;
      const [p, d] = await Promise.all([api.prefs(), api.devices()]);
      setPrefs(p.events || []);
      setDevices(d.devices || []);
      setHere(await syncPushDevice(d.devices || []));
    } catch (e: any) { setLoadErr((e && e.message) || "Couldn't load notification settings."); }
  }
  React.useEffect(() => { load(); }, []);

  async function turnOn() {
    setErr(""); setBusy("on");
    try {
      const r = await enablePush(api, cfg && cfg.publicKey, { register: !!import.meta.env.PROD });
      setSupport(readSupport());
      if (!r.ok) { setErr(enableProblem(r)); return; }
      notify("Notifications are on for this device.", { kind: "success" });
      await load();
    } finally { setBusy(""); }
  }
  async function turnOff() {
    setErr(""); setBusy("off");
    try { await disablePush(api); notify("Notifications are off for this device."); await load(); }
    finally { setBusy(""); }
  }
  async function sendTest() {
    setErr(""); setBusy("test");
    try {
      const r = await api.test();
      if (!r.devices) notify("No devices are turned on yet.", { kind: "info" });
      else if (r.sent) notify(`Test sent to ${r.sent} device${r.sent === 1 ? "" : "s"}${r.removed ? ` · removed ${r.removed} that no longer take notifications` : ""}.`, { kind: "success" });
      else setErr(r.removed ? "Those devices no longer take notifications, so they were removed. Turn this device on again." : "The test didn't go through. Try again in a minute.");
      await load();
    } catch (e: any) { setErr((e && e.message) || "Couldn't send a test."); }
    finally { setBusy(""); }
  }
  async function toggle(key: string, on: boolean) {
    setPrefs((ps: any) => (ps || []).map((p: any) => (p.key === key ? { ...p, on } : p)));
    try { const r = await api.setPrefs({ [key]: on }); setPrefs(r.events || []); }
    catch (e: any) { setErr((e && e.message) || "Couldn't save that."); load(); }
  }
  async function remove(d: any) {
    const mine = d.key && d.key === here.key;
    if (!(await confirmDialog({ title: `Remove ${d.device}?`, body: mine ? "This device stops getting notifications. You can turn it back on here any time." : "That device stops getting notifications. Turn it on again from the device itself.", confirmLabel: "Remove", danger: true }))) return;
    try {
      if (mine) await disablePush(api);
      else await api.removeDevice(d.id);
      await load();
    } catch (e: any) { setErr((e && e.message) || "Couldn't remove that device."); }
  }

  const intro = <p className="muted push-lede">Get a buzz on your phone or computer the moment a lead comes in, a quote is signed or a payment lands. Faster than email, and free.</p>;

  if (!api) {
    return (
      <div className="card pad push-card">
        <div className="kicker kicker-teal row gap-8"><Icon name="bell" size={14} />Notifications</div>
        {intro}
        <p className="push-note"><Icon name="bell" size={14} />{inSandbox() ? "The sandbox doesn't send notifications. Create your account to get them on your phone." : "Notifications need the live service — open app.fenceworks.ai and sign in."}</p>
      </div>
    );
  }
  const role = roleOf(store);
  const ready = cfg && cfg.enabled && cfg.setup !== false;
  const noEvents = prefs && prefs.length === 0;

  return (
    <div className="push-panel">
      <div className="card pad push-card">
        <div className="row between" style={{ gap: 8, flexWrap: "wrap" }}>
          <div className="kicker kicker-teal row gap-8"><Icon name="bell" size={14} />This device</div>
          {ready && support.state === "ready" && (
            <span className={"push-state" + (here.subscribed ? " on" : "")} data-testid="push-state">
              <span className="dot" />{here.subscribed ? "On" : "Off"} · {thisDeviceLabel()}
            </span>
          )}
        </div>
        {intro}
        {loadErr && <p className="push-err">{loadErr} <button className="btn btn-ghost btn-sm" onClick={load}>Try again</button></p>}
        {!cfg && !loadErr && <p className="muted" style={{ margin: 0 }}>Loading…</p>}
        {cfg && cfg.setup === false && <p className="push-note"><Icon name="bell" size={14} />Notifications aren't ready on the server yet: run migration #{cfg.migration || 37} (supabase/migrate_2026-10-15_push.sql).</p>}
        {cfg && cfg.setup !== false && !cfg.enabled && (
          <p className="push-note"><Icon name="bell" size={14} /><span>Phone notifications aren't switched on for Fence Works yet. Email and text alerts work as before.{cfg.message ? <span className="faint" style={{ display: "block", fontSize: 11.5, marginTop: 3 }}>{cfg.message}</span> : null}</span></p>
        )}
        {ready && support.state !== "ready" && <SupportNote support={support} />}
        {ready && support.state === "ready" && (
          <div className="row gap-8 push-actions">
            {here.subscribed
              ? <button className="btn btn-ghost" disabled={!!busy} onClick={turnOff}><Icon name="x" size={14} />{busy === "off" ? "Turning off…" : "Turn off on this device"}</button>
              : <button className="btn btn-primary" disabled={!!busy || noEvents} onClick={turnOn}><Icon name="bell" size={15} />{busy === "on" ? "Turning on…" : "Turn on for this device"}</button>}
            {devices && devices.length > 0 && <button className="btn btn-outline" disabled={!!busy} onClick={sendTest}><Icon name="send" size={14} />{busy === "test" ? "Sending…" : "Send a test"}</button>}
          </div>
        )}
        {ready && support.state === "ready" && !here.subscribed && !noEvents && <p className="faint push-fine">Your browser will ask to allow notifications. Choose <b>Allow</b>.</p>}
        {err && <p className="push-err" role="alert">{err}</p>}
      </div>

      {ready && (
        <div className="card pad push-card">
          <div className="kicker" style={{ marginBottom: 8 }}>Tell me when</div>
          {!prefs ? <p className="muted" style={{ margin: 0 }}>Loading…</p>
            : noEvents ? <p className="muted" style={{ margin: 0 }}>Nothing notifies the {roleLabel(role)} role. Ask the account owner if you need these alerts.</p>
            : (
              <div className="push-events">
                {prefs.map((p: any) => (
                  <button key={p.key} type="button" role="switch" aria-checked={!!p.on} className={"toggle-row" + (p.on ? " on" : "")} onClick={() => toggle(p.key, !p.on)}>
                    <span className={"sw" + (p.on ? " on" : "")}><span className="knob" /></span>
                    <span style={{ minWidth: 0 }}><b>{p.label}</b><span className="push-hint">{p.hint}</span></span>
                  </button>
                ))}
              </div>
            )}
          {prefs && !noEvents && <p className="faint push-fine">These apply to every device you turn on. A burst comes as one notification ("5 new leads"), and the lock screen only shows a name — never a phone number or address.</p>}
        </div>
      )}

      {ready && (
        <div className="card pad push-card">
          <div className="kicker" style={{ marginBottom: 8 }}>Your devices</div>
          {!devices ? <p className="muted" style={{ margin: 0 }}>Loading…</p>
            : devices.length === 0 ? <p className="muted" style={{ margin: 0 }}>None yet. Turn it on here, then on your phone: open app.fenceworks.ai, sign in, tap the bell, then Phone notifications.</p>
            : (
              <div className="push-devices">
                {devices.map((d: any) => (
                  <div key={d.id} className="push-device">
                    <Icon name={/iPhone|iPad|Android/.test(d.device) ? "phone" : "dashboard"} size={16} />
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <b>{d.device}</b>{d.key && d.key === here.key && <span className="chip teal" style={{ fontSize: 9.5, marginLeft: 6 }}>this device</span>}
                      <div className={"push-hint" + (d.failures > 0 ? " warn" : "")}>{deviceStatus(d)}</div>
                    </div>
                    <button className="btn btn-ghost btn-sm" onClick={() => remove(d)} aria-label={"Remove " + d.device}>Remove</button>
                  </div>
                ))}
              </div>
            )}
        </div>
      )}
    </div>
  );
}

/* The dashboard's soft ask. */
export function PushPromptCard({ store, go }: any) {
  const api = pushApi();
  const [cfg, setCfg] = React.useState<any>(null);
  const [hidden, setHidden] = React.useState<boolean>(false);
  const [busy, setBusy] = React.useState<boolean>(false);
  const [err, setErr] = React.useState<string>("");
  const support = React.useMemo(readSupport, []);
  const dismissedAt = React.useMemo(() => { try { return Number(localStorage.getItem(PROMPT_KEY)) || 0; } catch (e) { return 0; } }, []);
  // the store decides first (no lead or signed quote yet → no network call at all)
  const worthAsking = React.useMemo(() => showPromptCard({ store, support, config: { enabled: true, events: ["lead"] }, subscribed: pushOnHere(), dismissedAt }), [store && store.quotes, store && store.customers]);
  React.useEffect(() => {
    if (!api || !worthAsking || cfg) return;
    let alive = true;
    loadConfig(api).then((c) => { if (alive) setCfg(c); }).catch(() => {});
    return () => { alive = false; };
  }, [worthAsking]);
  if (!api || hidden || !worthAsking || !cfg) return null;
  if (!showPromptCard({ store, support, config: cfg, subscribed: pushOnHere(), dismissedAt })) return null;

  const later = () => { try { localStorage.setItem(PROMPT_KEY, String(Date.now())); } catch (e) {} setHidden(true); };
  async function turnOn() {
    setErr(""); setBusy(true);
    try {
      const r = await enablePush(api, cfg.publicKey, { register: !!import.meta.env.PROD });
      if (r.ok) { notify("Notifications are on for this device. Send yourself a test from the bell → Phone notifications.", { kind: "success" }); setHidden(true); return; }
      setErr(enableProblem(r));
    } finally { setBusy(false); }
  }
  const ios = support.state === "ios-install";
  return (
    <div className="card push-prompt" role="region" aria-label="Turn on notifications">
      <div className="push-prompt-ic"><Icon name="bell" size={18} /></div>
      <div className="push-prompt-main">
        <b>Get a buzz when leads come in</b>
        <p>{ios
          ? "Your iPhone can light up the moment a lead comes in, a quote is signed or a payment lands. Apple allows it once Fence Works is on your Home Screen."
          : "Your phone or computer lights up the moment a lead comes in, a quote is signed or a payment lands. Faster than email, and free."}</p>
        {err && <p className="push-err" role="alert">{err}</p>}
      </div>
      <div className="push-prompt-actions">
        {ios
          ? <button className="btn btn-primary btn-sm" onClick={() => go && go("notifications")}>Show me how</button>
          : <button className="btn btn-primary btn-sm" disabled={busy} onClick={turnOn}>{busy ? "Turning on…" : "Turn on"}</button>}
        <button className="btn btn-ghost btn-sm" onClick={later}>Not now</button>
      </div>
    </div>
  );
}

/* /notifications — the same panel as Account → Notifications, for everyone
   (Account is owner/admin only; a sales rep wants their leads too). The bell
   menu and a test notification open it. */
export function NotificationsPage({ store, go }: any) {
  return (
    <div className="page fade-up push-page">
      <h1 className="display" style={{ fontSize: 28, margin: "0 0 4px" }}>Notifications</h1>
      <p className="muted" style={{ margin: "0 0 18px", fontSize: 14.5 }}>Phone and desktop alerts for the moments that matter.</p>
      <NotificationsPanel store={store} go={go} />
    </div>
  );
}
